import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import academiesTranslations from '../../data/academies.json';
import { addAcademyTranslation, getAcademyTranslation, reloadTranslations, validateTranslationsFile, saveTranslationsToFile } from '../../services/academiesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { 
  GraduationCapIcon, 
  SearchIcon, 
  XCircleIcon,
  PlusIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon
} from 'lucide-react';

const Academies = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [academies, setAcademies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [countryId, setCountryId] = useState('');
  const [regionId, setRegionId] = useState('');
  
  // Add academy modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newAcademy, setNewAcademy] = useState({
    regionId: '',
    code: '',
    translations: {
      fr: '',
      en: '',
      ar: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit academy modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAcademy, setEditingAcademy] = useState({
    id: null,
    name: '',
    code: '',
    regionId: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Fetch countries for filter
  const fetchCountries = async () => {
    try {
      setCountriesLoading(true);
      console.log('Fetching countries...');
      
      // Try with smaller limit first
      const response = await api.get('/organization/countries?page=1&limit=50');
      console.log('Countries API response:', response.data);
      
      if (response.data.success) {
        console.log('Countries data:', response.data.data.countries);
        setCountries(response.data.data.countries);
        
        // If we got less than total, try to get more
        const total = response.data.data.pagination?.total || 0;
        if (total > 50) {
          console.log(`Total countries: ${total}, fetching more...`);
          try {
            const moreResponse = await api.get(`/organization/countries?page=1&limit=${Math.min(total, 200)}`);
            if (moreResponse.data.success) {
              setCountries(moreResponse.data.data.countries);
              console.log('Updated countries data:', moreResponse.data.data.countries);
            }
          } catch (moreError) {
            console.warn('Could not fetch all countries, using partial list:', moreError);
          }
        }
      } else {
        console.error('API returned success: false', response.data);
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if it's a validation error
      if (error.response?.data?.message === 'Validation error') {
        console.log('Validation error detected, trying different approach...');
        
        // Try without limit parameter
        try {
          const noLimitResponse = await api.get('/organization/countries?page=1');
          if (noLimitResponse.data.success) {
            setCountries(noLimitResponse.data.data.countries);
            console.log('No limit approach successful:', noLimitResponse.data.data.countries);
            return;
          }
        } catch (noLimitError) {
          console.error('No limit approach also failed:', noLimitError);
        }
      }
      
      // Try with even smaller limit as fallback
      try {
        console.log('Trying with smaller limit...');
        const fallbackResponse = await api.get('/organization/countries?page=1&limit=10');
        if (fallbackResponse.data.success) {
          setCountries(fallbackResponse.data.data.countries);
          console.log('Fallback successful:', fallbackResponse.data.data.countries);
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
      
      // Final fallback: try to use static data or set empty array
      console.log('All API attempts failed, using empty countries list');
      setCountries([]);
      
      // Show detailed error message to user
      const errorMessage = error.response?.data?.message || error.message || t('academies.countriesFetchError');
      console.error('Final error message:', errorMessage);
      toast.error(`${t('academies.countriesFetchError')}: ${errorMessage}`, { duration: 5000, position: 'top-center' });
    } finally {
      setCountriesLoading(false);
    }
  };

  // Fetch regions for filter
  const fetchRegions = async () => {
    try {
      setRegionsLoading(true);
      console.log('Fetching regions...');
      
      // Try with smaller limit first
      const response = await api.get('/organization/regions?page=1&limit=50');
      console.log('Regions API response:', response.data);
      
      if (response.data.success) {
        console.log('Regions data:', response.data.data.regions);
        setRegions(response.data.data.regions);
        
        // If we got less than total, try to get more
        const total = response.data.data.pagination?.total || 0;
        if (total > 50) {
          console.log(`Total regions: ${total}, fetching more...`);
          try {
            const moreResponse = await api.get(`/organization/regions?page=1&limit=${Math.min(total, 200)}`);
            if (moreResponse.data.success) {
              setRegions(moreResponse.data.data.regions);
              console.log('Updated regions data:', moreResponse.data.data.regions);
            }
          } catch (moreError) {
            console.warn('Could not fetch all regions, using partial list:', moreError);
          }
        }
      } else {
        console.error('API returned success: false', response.data);
        setRegions([]);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if it's a validation error
      if (error.response?.data?.message === 'Validation error') {
        console.log('Validation error detected, trying different approach...');
        
        // Try without limit parameter
        try {
          const noLimitResponse = await api.get('/organization/regions?page=1');
          if (noLimitResponse.data.success) {
            setRegions(noLimitResponse.data.data.regions);
            console.log('No limit approach successful:', noLimitResponse.data.data.regions);
            return;
          }
        } catch (noLimitError) {
          console.error('No limit approach also failed:', noLimitError);
        }
      }
      
      // Try with even smaller limit as fallback
      try {
        console.log('Trying with smaller limit...');
        const fallbackResponse = await api.get('/organization/regions?page=1&limit=10');
        if (fallbackResponse.data.success) {
          setRegions(fallbackResponse.data.data.regions);
          console.log('Fallback successful:', fallbackResponse.data.data.regions);
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
      
      // Final fallback: try to use static data or set empty array
      console.log('All API attempts failed, using empty regions list');
      setRegions([]);
      
      // Show detailed error message to user
      const errorMessage = error.response?.data?.message || error.message || t('academies.regionsFetchError');
      console.error('Final error message:', errorMessage);
      toast.error(`${t('academies.regionsFetchError')}: ${errorMessage}`, { duration: 5000, position: 'top-center' });
    } finally {
      setRegionsLoading(false);
    }
  };

  // Fetch academies - API: /academies, /regions/{id}/academies, /countries/{id}/academies
  const fetchAcademies = async (page = 1, search = '', filterRegionId = regionId, filterCountryId = countryId, itemsPerPage = limit) => {
    try {
      setLoading(true);
      let url = '/organization/academies';
      if (filterRegionId) url = `/organization/regions/${filterRegionId}/academies`;
      else if (filterCountryId) url = `/organization/countries/${filterCountryId}/academies`;
      const params = new URLSearchParams({ page: page.toString(), limit: itemsPerPage.toString() });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`${url}?${params.toString()}`);
      console.log('Academies API response:', response.data);
      
      if (response.data.success) {
        setAcademies(response.data.data.academies);
        setTotal(response.data.data.pagination.total);
        setCurrentPage(response.data.data.pagination.page);
      } else {
        throw new Error(response.data.message || 'Failed to fetch academies');
      }
    } catch (error) {
      console.error('Error fetching academies:', error);
      toast.error(t('academies.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegions(); fetchCountries(); }, []);

  useEffect(() => {
    if (searchTerm === '') {
      fetchAcademies(1, '', regionId, countryId);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchAcademies(1, searchTerm, regionId, countryId).finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, countryId, regionId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAcademies(1, searchTerm, regionId, countryId);
  };

  const clearFilters = () => {
    setCountryId('');
    setRegionId('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchAcademies(1, '', '', '', limit);
  };

  const getActiveFiltersCount = () => (countryId ? 1 : 0) + (regionId ? 1 : 0);

  const didMountAcademies = useRef(false);
  useEffect(() => {
    if (!didMountAcademies.current) { didMountAcademies.current = true; return; }
    setCurrentPage(1);
    fetchAcademies(1, searchTerm, regionId, countryId, limit);
  }, [countryId, regionId, limit]);

  // Translate academy name
  const translateAcademyName = (arabicName, language) => {
    return getAcademyTranslation(arabicName, language);
  };

  // Translate region name
  const translateRegionName = (arabicName, language) => {
    return getRegionTranslation(arabicName, language);
  };

  // Translate country name
  const translateCountryName = (arabicName, language) => {
    return getCountryTranslation(arabicName, language);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Handle add academy
  const handleAddAcademy = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    if (!newAcademy.regionId) {
      errors.regionId = t('academies.regionRequired');
    }
    if (!newAcademy.code.trim()) {
      errors.code = t('academies.codeRequired');
    } else if (newAcademy.code.trim().length < 2 || newAcademy.code.trim().length > 10) {
      errors.code = t('academies.codeLength');
    }
    if (!newAcademy.translations.fr.trim()) {
      errors.translation_fr = t('academies.nameRequired_fr');
    }
    if (!newAcademy.translations.en.trim()) {
      errors.translation_en = t('academies.nameRequired_en');
    }
    if (!newAcademy.translations.ar.trim()) {
      errors.translation_ar = t('academies.nameRequired_ar');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        regionId: parseInt(newAcademy.regionId),
        name: newAcademy.translations.ar.trim(), // Use Arabic name as primary name
        code: newAcademy.code.trim()
      };
      
      const response = await api.post('/organization/academies', requestData);
      
      if (response.data.success) {
        console.log('Academy created successfully with ID:', response.data.data?.academyId);
        
        // Save translations using the new system
        try {
          const translationResult = addAcademyTranslation(
            newAcademy.translations.ar.trim(),
            {
              fr: newAcademy.translations.fr.trim(),
              en: newAcademy.translations.en.trim(),
              ar: newAcademy.translations.ar.trim()
            }
          );

          if (translationResult.success) {
            console.log('Translation saved successfully:', translationResult.message);
            toast.success(response.data.message || t('academies.addSuccess'), { duration: 3000, position: 'top-center' });
          } else {
            toast.success(response.data.message || t('academies.addSuccess'), { duration: 3000, position: 'top-center' });
            console.warn('Academy added but translation save failed:', translationResult.message);
          }
          
          setNewAcademy({ regionId: '', code: '', translations: { fr: '', en: '', ar: '' } });
          setFormErrors({});
          setShowAddModal(false);
          
          // Refresh the list
          fetchAcademies(1, searchTerm, regionId, countryId);
        } catch (translationError) {
          console.error('Translation save error:', translationError);
          toast.success(response.data.message || t('academies.addSuccess'), { duration: 3000, position: 'top-center' });
          console.warn('Academy added but translation save failed');
        }
      } else {
        throw new Error(response.data.message || t('academies.addError'));
      }
    } catch (error) {
      console.error('Error adding academy:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('academies.addError');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 5000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setNewAcademy(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle translation changes
  const handleTranslationChange = (language, value) => {
    setNewAcademy(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value
      }
    }));
    
    const errorKey = `translation_${language}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  // Open add modal
  const openAddModal = () => {
    setNewAcademy({ regionId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewAcademy({ regionId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
  };

  // Handle export translations
  const handleExportTranslations = () => {
    const result = saveTranslationsToFile();
    if (result.success) {
      toast.success(result.message, {
        duration: 5000,
        position: 'top-center'
      });
    } else {
      toast.error(result.message, {
        duration: 3000,
        position: 'top-center'
      });
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (academyId, currentStatus) => {
    try {
      const response = await api.put(`/academies/${academyId}/toggle-status`, {
        is_active: currentStatus === 1 ? 0 : 1
      });
      
      if (response.data.success) {
        toast.success(t('academies.statusUpdated'), { duration: 3000, position: 'top-center' });
        fetchAcademies(currentPage, searchTerm, regionId, countryId);
      } else {
        throw new Error(response.data.message || t('academies.statusUpdateError'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('academies.statusUpdateError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Handle edit academy
  const handleEditAcademy = (academy) => {
    setEditingAcademy({
      id: academy.id,
      name: academy.name,
      code: academy.code,
      regionId: academy.region_id || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Update academy
  const updateAcademy = async () => {
    try {
      setIsEditing(true);
      setEditFormErrors({});

      // Validation
      const errors = {};
      if (!editingAcademy.name.trim()) {
        errors.name = t('academies.nameRequired');
      }
      if (!editingAcademy.code.trim()) {
        errors.code = t('academies.codeRequired');
      }
      if (editingAcademy.code.trim().length < 2 || editingAcademy.code.trim().length > 10) {
        errors.code = t('academies.codeLength');
      }
      if (!editingAcademy.regionId) {
        errors.regionId = t('academies.regionRequired');
      }

      if (Object.keys(errors).length > 0) {
        setEditFormErrors(errors);
        return;
      }

      const response = await api.put(`/organization/academies/${editingAcademy.id}`, {
        name: editingAcademy.name.trim(),
        code: editingAcademy.code.trim(),
        regionId: parseInt(editingAcademy.regionId)
      });

      if (response.data.success) {
        toast.success(t('academies.updateSuccess'));
        setShowEditModal(false);
        setEditingAcademy({ id: null, name: '', code: '', regionId: '' });
        fetchAcademies(currentPage, searchTerm, regionId, countryId);
      } else {
        throw new Error(response.data.message || t('academies.updateError'));
      }
    } catch (error) {
      console.error('Error updating academy:', error);
      const errorMessage = error.response?.data?.message || error.message || t('academies.updateError');
      toast.error(errorMessage);
    } finally {
      setIsEditing(false);
    }
  };

  // Delete academy
  const deleteAcademy = async (academyId, academyName) => {
    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `${t('academies.deleteConfirm')} "${academyName}"?`
      );
      
      if (!confirmed) {
        return;
      }

      const response = await api.delete(`/organization/academies/${academyId}`);
      
      if (response.data.success) {
        toast.success(t('academies.deleteSuccess'));
        fetchAcademies(currentPage, searchTerm, regionId, countryId);
      } else {
        throw new Error(response.data.message || t('academies.deleteError'));
      }
    } catch (error) {
      console.error('Error deleting academy:', error);
      const errorMessage = error.response?.data?.message || error.message || t('academies.deleteError');
      toast.error(errorMessage);
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAcademies(newPage, searchTerm, regionId, countryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <GraduationCapIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('academies.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('academies.subtitle')}
              </p>
            </div>
          </div>
          <div className={`flex ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('academies.addAcademy')}
            </button>
            <button 
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <GlobeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              Export Translations
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - API: page, limit, search, regionId/countryId (path) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('academies.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-40">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('academies.country')}</label>
              <select value={countryId} onChange={(e) => setCountryId(e.target.value)} disabled={countriesLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{countriesLoading ? t('academies.loadingCountries') : t('academies.allCountries')}</option>
                {countries.map((country) => <option key={country.id} value={country.id}>{translateCountryName(country.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-40">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('academies.region')}</label>
              <select value={regionId} onChange={(e) => setRegionId(e.target.value)} disabled={regionsLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{regionsLoading ? t('academies.loadingRegions') : t('academies.allRegions')}</option>
                {regions.map((region) => <option key={region.id} value={region.id}>{translateRegionName(region.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {(countryId || regionId || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Academies Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.academy')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.code')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.region')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.status')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.createdAt')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('academies.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {academies.map((academy) => (
                <tr key={academy.id} className="hover:bg-gray-50">
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <GraduationCapIcon className="h-4 w-4 text-primary-600" />
                        </div>
                      </div>
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm font-medium text-gray-900">
                          {translateAcademyName(academy.name, currentLanguage || 'fr')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {academy.code}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-900">
                      {translateRegionName(academy.region_name, currentLanguage || 'fr')}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <button
                      onClick={() => handleStatusToggle(academy.id, academy.is_active)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        academy.is_active === 1
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors cursor-pointer`}
                    >
                      {academy.is_active === 1 ? t('academies.active') : t('academies.inactive')}
                    </button>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {formatDate(academy.created_at)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-start space-x-2'}`}>
                      <button className="text-primary-600 hover:text-primary-900 p-1" title={t('academies.view')}>
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditAcademy(academy)}
                        className="text-blue-600 hover:text-blue-900 p-1" 
                        title={t('academies.edit')}
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => deleteAcademy(academy.id, academy.name)}
                        className="text-red-600 hover:text-red-900 p-1" 
                        title={t('academies.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={`bg-white px-4 py-3 flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} border-t border-gray-200`}>
          <div className={`flex-1 flex ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} sm:hidden`}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('academies.previous')}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage * limit >= total}
              className={`${isRTL ? 'mr-3' : 'ml-3'} relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                currentPage * limit >= total ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('academies.next')}
            </button>
          </div>
          <div className={`hidden sm:flex-1 sm:flex sm:items-center ${isRTL ? 'sm:justify-between flex-row-reverse' : 'sm:justify-between'}`}>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-sm text-gray-700">
                {t('academies.showing')} <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> {t('academies.to')}{' '}
                <span className="font-medium">{Math.min(currentPage * limit, total)}</span>{' '}
                {t('academies.of')} <span className="font-medium">{total}</span> {t('academies.results')}
                {getActiveFiltersCount() > 0 && (
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-primary-600`}>
                    ({getActiveFiltersCount()} {t('academies.filtersApplied')})
                  </span>
                )}
              </p>
            </div>
            <div>
              <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-l-md' : 'rounded-r-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {Array.from({ length: Math.min(5, Math.ceil(total / limit)) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage * limit >= total}
                  className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-r-md' : 'rounded-l-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    currentPage * limit >= total ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Add Academy Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
              <h3 className="text-lg font-medium text-gray-900">
                {t('academies.addAcademy')}
              </h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">{t('common.close')}</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddAcademy} className="space-y-4">
              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('academies.region')} *
                </label>
                <select
                  value={newAcademy.regionId}
                  onChange={(e) => handleInputChange('regionId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.regionId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={regionsLoading}
                >
                  <option value="">
                    {regionsLoading ? t('academies.loadingRegions') : t('academies.selectRegion')}
                  </option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {translateRegionName(region.name, currentLanguage || 'fr')}
                    </option>
                  ))}
                </select>
                {formErrors.regionId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.regionId}</p>
                )}
              </div>

              {/* Academy Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('academies.code')} *
                </label>
                <input
                  type="text"
                  value={newAcademy.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.code ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ex: AUH01"
                  maxLength={10}
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              {/* Translation Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* French Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇫🇷 Français *
                  </label>
                  <input
                    type="text"
                    value={newAcademy.translations.fr}
                    onChange={(e) => handleTranslationChange('fr', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_fr ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Académie d'Abu Dhabi"
                  />
                  {formErrors.translation_fr && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                  )}
                </div>

                {/* English Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇺🇸 English *
                  </label>
                  <input
                    type="text"
                    value={newAcademy.translations.en}
                    onChange={(e) => handleTranslationChange('en', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_en ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Abu Dhabi Academy"
                  />
                  {formErrors.translation_en && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                  )}
                </div>

                {/* Arabic Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇸🇦 العربية *
                  </label>
                  <input
                    type="text"
                    value={newAcademy.translations.ar}
                    onChange={(e) => handleTranslationChange('ar', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_ar ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="مثال: أكاديمية أبوظبي"
                  />
                  {formErrors.translation_ar && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'flex-row space-x-3'} pt-4`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAdding && (
                    <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  )}
                  {isAdding ? t('academies.adding') : t('academies.addAcademy')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Academy Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('academies.editAcademy')}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); updateAcademy(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('academies.academyName')} *
                  </label>
                  <input
                    type="text"
                    value={editingAcademy.name}
                    onChange={(e) => setEditingAcademy(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('academies.academyName')}
                  />
                  {editFormErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('academies.academyCode')} *
                  </label>
                  <input
                    type="text"
                    value={editingAcademy.code}
                    onChange={(e) => setEditingAcademy(prev => ({ ...prev, code: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('academies.academyCode')}
                    maxLength={10}
                  />
                  {editFormErrors.code && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.code}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('academies.region')} *
                  </label>
                  <select
                    value={editingAcademy.regionId}
                    onChange={(e) => setEditingAcademy(prev => ({ ...prev, regionId: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.regionId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={regionsLoading}
                  >
                    <option value="">
                      {regionsLoading ? t('academies.loadingRegions') : t('academies.selectRegion')}
                    </option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {translateRegionName(region.name, currentLanguage || 'fr')}
                      </option>
                    ))}
                  </select>
                  {editFormErrors.regionId && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.regionId}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {isEditing ? t('academies.updating') : t('academies.update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Academies;
